// ============================================================
// W'ILY'S PIER — THE CREST WELL  (v2, N-way)
//
// The ONE exchange in the whole Illegal Empire. Every title's
// currency flows into Divine Crests here; DC flows back out into
// any other title's currency. No other app may ever build one of
// these. That rule is absolute.
//
// WHAT CHANGED FROM v1:
//   • Was a hardwired two-way DC<->SE swap. Now an N-way hub — any
//     registered currency to any other.
//   • Rates are NEVER hardcoded here. They stream live from
//     pierre/exchange/rates/ so caP'n can retune without a deploy.
//   • Cross-currency (CC -> SE) routes through DC as TWO LEGS and
//     we SHOW BOTH. We never hide the routing.
//   • The 5% Pierre cut is gone. There is no fee. The Well is a
//     service the Empire provides its Aenarchists, not a business.
//
// All write logic lives in progression.js — this file is pure UI
// over it, and does no arithmetic Firebase can't verify.
//
// Props:
//   uid      — player uid
//   profile  — live profile (read for balances)
//   wallet   — optional live { DC, CC, SE, ... } map from the Pier.
//              Falls back to reading the profile directly.
//   onClose  — dismiss handler
// ============================================================

import { useState, useEffect, useMemo } from "react";
import { SD } from "./theme";
import {
  CURRENCIES,
  DC_ID,
  tintFor,
  planExchange,
  minimumFor,
  executeExchange,
  listenToRates,
  ensureRateTable,
  listenToExchangeCooldown,
  getExchangeHistory,
  WELL_TESTING,
  TEST_DC_OUT,
  TEST_DC_IN,
} from "./progression";
import { isAcquireOnly, ACQUIRE_ONLY_REASON } from "./upDawgs";

// ── small helpers ─────────────────────────────────────────────
const fmt = (n) => (n || 0).toLocaleString();

const formatCooldown = (ms) => {
  if (!ms || ms <= 0) return "";
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const timeAgo = (ts) => {
  if (!ts) return "";
  const diff = Date.now() - ts;
  if (diff < 60000)    return "just now";
  if (diff < 3600000)  return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
};

// Module-level mirror of the live rows, so a currency Pierre supplies in
// code (✹ UP Dawgs) gets its real glyph and name instead of "•".
let _rows = {};
const iconFor  = (id) => _rows[id]?.icon || CURRENCIES[id]?.icon || "•";
const labelFor = (id) => _rows[id]?.name || CURRENCIES[id]?.name || id;

export default function CrestExchange({ uid, profile, wallet, onClose }) {
  const [rates, setRates]           = useState(null);  // null = still loading
  const [fromId, setFromId]         = useState(null);
  const [toId, setToId]             = useState(DC_ID);
  const [amount, setAmount]         = useState("");
  const [cooldownMs, setCooldownMs] = useState(0);
  const [history, setHistory]       = useState([]);
  const [busy, setBusy]             = useState(false);
  const [flash, setFlash]           = useState(null);  // { ok, text }

  // ── balances ──────────────────────────────────────────────
  // Prefer the live wallet the Pier hands down; fall back to the
  // profile so the Well still works if opened standalone.
  //
  // ⚠ The wallet is the real source and the profile is only a
  //   standalone fallback — it CANNOT see a currency that lives outside
  //   profiles/ (Dewdrops are in the garden). That is why the fallback
  //   is per-currency rather than wholesale: a `fullPath` currency just
  //   reads 0 from the fallback and correct from the wallet, instead of
  //   the whole panel going blank.
  const balances = useMemo(() => {
    const out = {};
    const ids = new Set([
      ...Object.keys(CURRENCIES),
      ...Object.keys(rates || {}),
      ...Object.keys(wallet || {}),
    ]);
    for (const id of ids) {
      const meta = (rates && rates[id]) || CURRENCIES[id] || {};
      const fromProfile = meta.path ? (profile?.[meta.path] || 0) : 0;
      out[id] = wallet?.[id] ?? fromProfile;
    }
    return out;
  }, [wallet, profile, rates]);

  // ── the rate table: seed once if absent, then stream live ──
  useEffect(() => {
    let unsub;
    ensureRateTable().finally(() => {
      unsub = listenToRates((r) => { _rows = r || {}; setRates(r); });
    });
    return () => { if (typeof unsub === "function") unsub(); };
  }, []);

  // Source: DC, plus anything registered the player actually holds.
  // Target: DC, plus every registered currency.
  // ⟡ THE ROWS ARE THE LIST. (Recalibration, 2026-08-13.)
  //   These two filters used to also require `CURRENCIES[id]` — a
  //   hardcoded three-row const — which is what stranded ⋔ Kindling,
  //   ❦ Blood Money and ❋ Dewdrops outside the Well despite all three
  //   having rates. A registered row is now sufficient, which is what
  //   "registration is one write and zero deploys" has to mean if it is
  //   going to mean anything.
  // ⭐ GATE 3 OF 3 · THE ACQUIRE-ONLY LAW, at the counter.
  // An acquire-only currency is never offered as a SOURCE — it does not
  // appear in this list even when the player is holding a pile of it.
  // The other two gates (planExchange, executeExchange) already refuse
  // it; this one exists so a player is never shown a door that is going
  // to be shut in their face. A refusal you could have predicted from
  // the UI is a bug in the UI, not a rule doing its job.
  const sourceIds = useMemo(() => {
    if (!rates) return [];
    return [DC_ID, ...Object.keys(rates)]
      .filter((id) => !isAcquireOnly(id))
      .filter((id) => (balances[id] || 0) > 0);
  }, [rates, balances]);

  const targetIds = useMemo(() => {
    if (!rates) return [];
    return [DC_ID, ...Object.keys(rates)].filter((id) => id !== fromId);
  }, [rates, fromId]);

  // Pick a sensible opening pair once the rates land.
  useEffect(() => {
    if (!rates || fromId) return;
    const first = sourceIds.find((id) => id !== DC_ID) || sourceIds[0] || null;
    if (!first) return;
    setFromId(first);
    if (first === DC_ID) setToId(Object.keys(rates)[0] || DC_ID);
  }, [rates, sourceIds, fromId]);

  // Never let both sides be the same currency.
  useEffect(() => {
    if (fromId && toId === fromId) {
      setToId(fromId === DC_ID ? (Object.keys(rates || {})[0] || DC_ID) : DC_ID);
    }
  }, [fromId, toId, rates]);

  // ── live cooldown ─────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    const unsub = listenToExchangeCooldown(uid, setCooldownMs);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [uid]);

  useEffect(() => {
    if (cooldownMs <= 0) return;
    const t = setInterval(() => {
      setCooldownMs((ms) => (ms > 1000 ? ms - 1000 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownMs > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── history ───────────────────────────────────────────────
  const loadHistory = async () => {
    if (!uid) return;
    try { setHistory(await getExchangeHistory(uid)); }
    catch { /* no-op */ }
  };
  useEffect(() => { loadHistory(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── the plan ──────────────────────────────────────────────
  const numAmount = parseInt(amount, 10) || 0;
  const fromBal   = fromId ? (balances[fromId] || 0) : 0;
  const plan = (rates && fromId && toId && numAmount > 0)
    ? planExchange(fromId, toId, numAmount, rates)
    : null;

  const overBalance = numAmount > fromBal;
  const onCooldown  = cooldownMs > 0;
  const canSubmit   = !busy && !onCooldown && !!plan?.valid && !overBalance;

  // A rate caP'n hasn't measured yet gets said out loud, not buried.
  const provisional = !plan?.testing && (plan?.legs || []).some((l) => {
    const id = l.fromId === DC_ID ? l.toId : l.fromId;
    return rates?.[id]?.provisional;
  });

  // ── actions ───────────────────────────────────────────────
  const doExchange = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setFlash(null);
    try {
      const res = await executeExchange(uid, fromId, toId, numAmount);
      if (res?.success) {
        const left = res.remainder > 0
          ? ` · ${fmt(res.remainder)} ${res.fromId} left in your pocket`
          : "";
        setFlash({
          ok: true,
          text: `+${fmt(res.output)} ${iconFor(res.toId)} ${res.toId}${left}`,
        });
        setAmount("");
        loadHistory();
      } else {
        setFlash({ ok: false, text: res?.reason || "The Well refused the offering." });
      }
    } catch {
      setFlash({ ok: false, text: "Something went wrong at the Well." });
    } finally {
      setBusy(false);
    }
  };

  const swap = () => {
    if (!fromId || !toId) return;
    // ⚠ THE FLIP IS A SOURCE PICKER TOO. Swapping Crests→Dawgs would
    // otherwise put Dawgs in the offer slot by the back door, and the
    // player would meet a refusal they had no way to see coming.
    if (isAcquireOnly(toId)) { setFlash({ ok: false, text: ACQUIRE_ONLY_REASON }); return; }
    setFromId(toId);
    setToId(fromId);
    setAmount("");
    setFlash(null);
  };

  const setMax = () => {
    if (!fromId) return;
    setAmount(String(Math.max(0, Math.floor(fromBal))));
    setFlash(null);
  };

  // ── render ────────────────────────────────────────────────
  const loading = rates === null;
  const noRates = rates !== null && Object.keys(rates).length === 0;
  const broke   = rates !== null && sourceIds.length === 0;

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={panel}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{
            fontSize: 20, fontFamily: SD.fontDisplay, fontWeight: 900,
            color: SD.gold, letterSpacing: 3, textShadow: `0 0 16px ${SD.gold}`,
          }}>◆ THE CREST WELL</div>
          <div style={{
            fontSize: 9, color: SD.starDim, fontFamily: SD.fontDisplay,
            letterSpacing: 2, marginTop: 5, lineHeight: 1.7,
          }}>
            THE ONLY EXCHANGE IN THE EMPIRE<br />
            <span style={{ color: "#6ee7a8" }}>NO FEE · NOT NOW · NOT EVER</span>
            {WELL_TESTING && (<>
              <br />
              <span style={{ color: "#f0b040" }}>
                🧪 TESTING RATES · 1 ◆ → {TEST_DC_OUT} OF ANYTHING · 1 OF ANYTHING → {TEST_DC_IN} ◆ · EVERYTHING ELSE 1:1 · NO COOLDOWN
              </span>
            </>)}
          </div>
        </div>

        {/* Wallet */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {[DC_ID, ...Object.keys(rates || {}), ...Object.keys(CURRENCIES)]
            .filter((id, i, a) => a.indexOf(id) === i)
            .filter((id) => id === DC_ID || (balances[id] || 0) > 0 || rates?.[id])
            .map((id) => (
              <BalancePill
                key={id}
                icon={iconFor(id)}
                label={id}
                value={fmt(balances[id])}
                color={tintFor(id, rates)}
              />
            ))}
        </div>

        {loading && <Notice>Reading the Well's depths…</Notice>}

        {noRates && (
          <Notice tone="warn">
            No currency is registered at the Well yet. A rate row belongs at
            pierre/exchange/rates/ — tell caP'n.
          </Notice>
        )}

        {!loading && !noRates && broke && (
          <Notice>
            You're carrying nothing the Well will take. Go earn something out
            there, then come back — I'll turn it into Crests.
          </Notice>
        )}

        {!loading && !noRates && !broke && (
          <>
            <div style={card}>
              <PickerRow
                caption="YOU OFFER"
                ids={sourceIds}
                active={fromId}
                onPick={(id) => { setFromId(id); setAmount(""); setFlash(null); }}
                balances={balances}
                rates={rates}
              />

              <div style={{ textAlign: "center", margin: "10px 0 6px" }}>
                <button onClick={swap} title="Flip the offering" style={swapBtn}>⇅</button>
              </div>

              <PickerRow
                caption="YOU RECEIVE"
                ids={targetIds}
                active={toId}
                onPick={(id) => { setToId(id); setFlash(null); }}
                balances={balances}
                rates={rates}
                showBalance={false}
              />

              {/* One-way currencies say so at the counter, before the
                  player has typed an amount — not after. */}
              {isAcquireOnly(toId) && (
                <div style={{
                  marginTop: 8, padding: "7px 9px", borderRadius: 4,
                  border: "1px solid #3a4a2e", background: "#141c0e",
                  fontSize: 10, lineHeight: 1.5, color: "#9ad46a",
                  fontFamily: "'Courier New', monospace", letterSpacing: ".02em",
                }}>
                  ONE WAY · {ACQUIRE_ONLY_REASON}
                </div>
              )}

              {/* Amount */}
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <input
                  type="number"
                  inputMode="numeric"
                  value={amount}
                  min={1}
                  onChange={(e) => { setAmount(e.target.value); setFlash(null); }}
                  placeholder={fromId ? `Min ${fmt(minimumFor(fromId, rates))} ${fromId}` : "Amount"}
                  style={inputStyle}
                />
                <button onClick={setMax} style={maxBtn}>MAX</button>
              </div>

              {/* The routing — shown, never hidden */}
              <div style={{ minHeight: 24, marginTop: 14 }}>
                {plan?.valid && (
                  <div>
                    {plan.legs.map((leg, i) => (
                      <div key={i} style={legRow}>
                        <span style={{ color: tintFor(leg.fromId, rates), fontWeight: 700 }}>
                          {iconFor(leg.fromId)} {fmt(leg.input)}
                        </span>
                        <span style={{ color: SD.starDim, margin: "0 8px" }}>→</span>
                        <span style={{ color: tintFor(leg.toId, rates), fontWeight: 900 }}>
                          {iconFor(leg.toId)} {fmt(leg.output)}
                        </span>
                        {plan.legs.length > 1 && (
                          <span style={{ color: SD.starDim, fontSize: 8, marginLeft: 8, letterSpacing: 1 }}>
                            LEG {i + 1}
                          </span>
                        )}
                      </div>
                    ))}

                    {plan.legs.length > 1 && (
                      <div style={{ textAlign: "center", fontSize: 8, color: SD.starDim, letterSpacing: 1, marginTop: 4 }}>
                        ROUTED THROUGH {fmt(plan.crests)} ◆ · ONE COOLDOWN, NO FEE
                      </div>
                    )}

                    {plan.remainder > 0 && (
                      <div style={{ textAlign: "center", fontSize: 9, color: "#7fd8ff", marginTop: 6 }}>
                        {fmt(plan.remainder)} {fromId} won't be needed — it stays yours.
                      </div>
                    )}

                    {provisional && (
                      <div style={{ textAlign: "center", fontSize: 9, color: "#f0b040", marginTop: 6 }}>
                        ⚠ This rate is provisional — not yet measured. It will move.
                      </div>
                    )}
                  </div>
                )}

                {plan && !plan.valid && <div style={errText}>{plan.reason}</div>}
                {overBalance && <div style={errText}>Not enough {labelFor(fromId)}.</div>}
              </div>
            </div>

            {/* Flash */}
            {flash && (
              <div style={{
                textAlign: "center", fontSize: 12, marginBottom: 12,
                fontFamily: SD.fontDisplay, letterSpacing: 1,
                color: flash.ok ? "#6ee7a8" : "#ff6b8a",
              }}>{flash.text}</div>
            )}

            {/* Submit */}
            <button onClick={doExchange} disabled={!canSubmit} style={{
              width: "100%", padding: 14,
              background: canSubmit
                ? `linear-gradient(135deg, ${SD.gold}33, ${SD.gold}55)`
                : "rgba(255,255,255,0.05)",
              border: `1px solid ${canSubmit ? SD.gold : SD.void4}`,
              borderRadius: 10, cursor: canSubmit ? "pointer" : "default",
              color: canSubmit ? SD.goldBright : SD.starDim,
              fontSize: 13, fontWeight: 900, fontFamily: SD.fontDisplay,
              letterSpacing: 3,
              boxShadow: canSubmit ? `0 0 20px ${SD.gold}33` : "none",
            }}>
              {busy ? "…" : onCooldown ? `SETTLING ${formatCooldown(cooldownMs)}` : "◆ OFFER TO THE WELL"}
            </button>

            {onCooldown && (
              <div style={{
                textAlign: "center", fontSize: 10, color: SD.starDim,
                fontFamily: SD.fontDisplay, marginTop: 8, letterSpacing: 1,
              }}>The Well must settle before it takes another offering.</div>
            )}
          </>
        )}

        {/* History */}
        {history.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{
              fontSize: 9, letterSpacing: 3, color: SD.starDim,
              fontFamily: SD.fontDisplay, marginBottom: 8, textTransform: "uppercase",
            }}>Recent Offerings</div>
            <div style={{ maxHeight: 132, overflowY: "auto" }}>
              {history.map((h) => {
                // v1 rows only carry `type`; v2 rows carry fromId/toId.
                const f = h.fromId || (h.type === "se_to_dc" ? "SE" : "DC");
                const t = h.toId   || (h.type === "se_to_dc" ? "DC" : "SE");
                return (
                  <div key={h.timestamp} style={histRow}>
                    <span>
                      {iconFor(f)} {fmt(h.input)}
                      <span style={{ color: SD.starDim, margin: "0 6px" }}>→</span>
                      <span style={{ color: tintFor(t, rates), fontWeight: 900 }}>
                        {iconFor(t)} {fmt(h.output)}
                      </span>
                      {h.legs > 1 && (
                        <span style={{ color: SD.starDim, fontSize: 8, marginLeft: 6 }}>via ◆</span>
                      )}
                    </span>
                    <span style={{ color: SD.starDim, fontSize: 9 }}>{timeAgo(h.timestamp)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Close */}
        <button onClick={onClose} style={{
          width: "100%", padding: 10, marginTop: 16,
          background: "transparent", border: `1px solid ${SD.void4}`,
          borderRadius: 8, cursor: "pointer",
          color: SD.starDim, fontSize: 10,
          fontFamily: SD.fontDisplay, letterSpacing: 2,
        }}>← BACK TO THE PIER</button>
      </div>
    </div>
  );
}

// ── presentational bits ───────────────────────────────────────
function PickerRow({ caption, ids, active, onPick, balances, rates, showBalance = true }) {
  return (
    <div>
      <div style={{
        fontSize: 8, letterSpacing: 3, color: SD.starDim,
        fontFamily: SD.fontDisplay, marginBottom: 6,
      }}>{caption}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {ids.map((id) => {
          const on = id === active;
          const tint = tintFor(id, rates);
          return (
            <button
              key={id}
              onClick={() => onPick(id)}
              style={{
                flex: "1 1 auto", minWidth: 84,
                background: on ? `${tint}22` : "rgba(255,255,255,0.03)",
                border: `1px solid ${on ? tint : SD.void4}`,
                borderRadius: 9, padding: "8px 6px", cursor: "pointer",
                fontFamily: SD.fontDisplay, textAlign: "center",
                boxShadow: on ? `0 0 14px ${tint}33` : "none",
              }}
            >
              <div style={{ fontSize: 15, color: tint }}>{iconFor(id)}</div>
              <div style={{
                fontSize: 9, fontWeight: 900, letterSpacing: 1,
                color: on ? tint : SD.starSilver, marginTop: 2,
              }}>{id}</div>
              <div style={{ fontSize: 7, color: SD.starDim, marginTop: 1, letterSpacing: .5 }}>
                {labelFor(id)}
              </div>
              {showBalance && (
                <div style={{ fontSize: 8, color: SD.starDim, marginTop: 2 }}>
                  {(balances?.[id] || 0).toLocaleString()}
                </div>
              )}
              {!WELL_TESTING && rates?.[id]?.provisional && (
                <div style={{ fontSize: 7, color: "#f0b040", marginTop: 1 }}>PROV.</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BalancePill({ icon, label, value, color }) {
  return (
    <div style={{
      flex: "1 1 88px", display: "flex", alignItems: "center",
      justifyContent: "center", gap: 5,
      background: "rgba(5,3,20,0.6)", border: `1px solid ${color}66`,
      borderRadius: 10, padding: "7px 8px",
      fontFamily: SD.fontDisplay,
    }}>
      <span style={{ fontSize: 12, color }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 900, color }}>{value}</span>
      <span style={{ fontSize: 7, color: SD.starDim, letterSpacing: 1.5 }}>{label}</span>
    </div>
  );
}

function Notice({ children, tone = "calm" }) {
  return (
    <div style={{
      textAlign: "center", fontSize: 11, lineHeight: 1.7,
      fontFamily: SD.fontDisplay,
      color: tone === "warn" ? "#f0b040" : SD.starDim,
      padding: "18px 10px",
    }}>{children}</div>
  );
}

// ── styles ────────────────────────────────────────────────────
const overlay = {
  position: "fixed", inset: 0, zIndex: 300,
  background: "rgba(0,0,0,0.85)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 16,
};
const panel = {
  width: "min(440px, 94vw)", maxHeight: "92vh", overflowY: "auto",
  background: "rgba(5,3,20,0.98)",
  border: `1px solid ${SD.gold}66`, borderRadius: 16,
  padding: "26px 24px",
  boxShadow: `0 0 44px ${SD.gold}22`,
  fontFamily: SD.fontDisplay,
};
const card = {
  background: "rgba(255,255,255,0.03)",
  border: `1px solid ${SD.void4}`, borderRadius: 12,
  padding: 16, marginBottom: 14,
};
const swapBtn = {
  background: "rgba(255,255,255,0.05)", border: `1px solid ${SD.void4}`,
  borderRadius: 8, width: 34, height: 30, cursor: "pointer",
  color: SD.starWhite, fontSize: 14,
};
const inputStyle = {
  flex: 1, boxSizing: "border-box",
  background: "rgba(255,255,255,0.05)", border: `1px solid ${SD.void4}`,
  borderRadius: 8, padding: "11px 12px",
  color: SD.starWhite, fontSize: 15, fontFamily: SD.fontDisplay,
  letterSpacing: 1, outline: "none", textAlign: "center",
};
const maxBtn = {
  background: "rgba(255,255,255,0.05)", border: `1px solid ${SD.void4}`,
  borderRadius: 8, padding: "0 14px", cursor: "pointer",
  color: SD.starDim, fontSize: 10, fontFamily: SD.fontDisplay, letterSpacing: 1,
};
const legRow = {
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 12, fontFamily: SD.fontDisplay, padding: "2px 0",
};
const errText = {
  fontSize: 11, color: "#ff6b8a", fontFamily: SD.fontDisplay,
  textAlign: "center",
};
const histRow = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  fontSize: 11, fontFamily: SD.fontDisplay,
  padding: "6px 8px", borderBottom: `1px solid ${SD.void4}`,
  color: SD.starSilver,
};
